// src/components/editor/StoryElementsPanel.jsx
//
// The editor's Story Elements tab: two views over one manuscript.
//
//   Outline  — the relationships themselves, no sections
//   Elements — four sections: Chapters, Scenes, Actors, Artifacts
//
// Chapters are a section like the others but are NOT stored with the others.
// They're derived from the manuscript's heading blocks by src/utils/outline.js,
// so the Chapters section renders the existing NavigationPanel rather than a
// second list that would drift from the document. Every chapter tool the panel
// has always carried — jump, insert, rename, delete, promote, demote, drag to
// reorder — comes along unchanged.
//
// Selecting any row moves the manuscript to where that object lives, not just
// chapters: an actor jumps to the first chapter it appears in, and its location
// tag walks through the rest one click at a time.
//
// NOTE: the panel container (.sidebar-panel-vertical) ships aggressive global
// CSS that forces any MUI Button/IconButton to full width with gold styling, so
// every control here is a plain <Box> those rules don't target.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Menu, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavigationPanel from './NavigationPanel';
import StoryElementsTabs from './StoryElementsTabs';
import StoryElementModal from './StoryElementModal';
import StoryElementDetails from './StoryElementDetails';
import { storyElementApi } from '../../utils/readerApi';
import {
  describeRef, flattenOutline, locationsFor, parseRef, relationshipSentences,
} from '../../utils/storyElements';
import { editorStructureRows } from '../../utils/manuscriptStructure';
import { orFallback } from '../../utils/apiError';

const GOLD = '#F5D76E';

// One dot colour per element type, so a row is identifiable at a glance without
// reading its section header — the Outline view mixes types in one list.
const TYPE_DOT = {
  chapter: '#8E86C9',
  scene: GOLD,
  actor: '#A99BE8',
  artifact: '#E6A0B5',
  // Manuscript pages are not story elements and do not belong to the board;
  // a colour of their own keeps that visible in a mixed list. Mirrored in
  // src/components/corkboard/corkTokens.js only if these rows ever reach the
  // board, which they do not today.
  structure: '#7FB3A8',
};

const ELEMENT_SECTIONS = [
  { type: 'scene', label: 'Scenes' },
  { type: 'actor', label: 'Actors' },
  { type: 'artifact', label: 'Artifacts' },
];

/**
 * A front- or back-matter page the author switched on in Manuscript Structure.
 *
 * Read-only on purpose: the content is templated fields owned by the Structure
 * page, so this row reports that the page is in the book and takes you there to
 * change it. It is a link, not a selection — the panel's selection vocabulary is
 * "node:<id>" and "chapter:<blockId>", both of which an edge endpoint can name,
 * and a structure element can be neither.
 */
function StructureRow({ name, onOpen }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      title={`${name} — open in Manuscript Structure`}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, width: '100%', textAlign: 'left',
        font: 'inherit', fontSize: 12.5, cursor: 'pointer', border: 'none',
        px: 2, py: 0.9,
        color: 'rgba(255,255,255,0.8)',
        backgroundColor: 'transparent',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)', color: 'white' },
        '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: '-2px' },
      }}
    >
      <Box component="span" sx={{
        width: 7, height: 7, borderRadius: '2px', flexShrink: 0,
        backgroundColor: TYPE_DOT.structure,
      }} />
      <Box component="span" sx={{
        flex: 1, minWidth: 0, overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {name}
      </Box>
      {/* Stands in for the LocationTag the story-element rows carry: same slot,
          same size, saying where this one lives instead of where it appears. */}
      <Box component="span" sx={{
        flexShrink: 0, fontSize: 9.5, color: 'rgba(255,255,255,0.35)',
      }}>
        Structure ↗
      </Box>
    </Box>
  );
}

function SectionHeader({ label, count }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1.5, pb: 0.75 }}>
      <Typography
        component="h3"
        sx={{
          color: 'rgba(255,255,255,0.45)', fontSize: 10.5, fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
        }}
      >
        {label} <Box component="span" sx={{ color: 'rgba(255,255,255,0.3)' }}>({count})</Box>
      </Typography>
    </Box>
  );
}

/**
 * Where this element appears in the manuscript, and the control that walks you
 * there. One location jumps; several cycle, one chapter per click, showing
 * which of them you are looking at.
 */
function LocationTag({ locations, navIndex, onCycle }) {
  const count = locations.length;
  if (!count) {
    return (
      <Box
        component="span"
        title="Not connected to any chapter yet"
        sx={{ flexShrink: 0, fontSize: 9.5, color: 'rgba(255,255,255,0.3)' }}
      >
        unplaced
      </Box>
    );
  }
  const cycling = navIndex != null;
  return (
    <Box
      component="span"
      role="button"
      tabIndex={0}
      aria-label={cycling
        ? `Viewing ${locations[navIndex].name}. Go to next of ${count}`
        : `Go to where this appears (${count})`}
      title="Go to where this appears in the manuscript"
      onClick={(e) => { e.stopPropagation(); onCycle(); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onCycle(); }
      }}
      sx={{
        flexShrink: 0, fontSize: 9.5, px: 0.75, borderRadius: '999px', cursor: 'pointer',
        color: GOLD, backgroundColor: 'rgba(245,215,110,0.14)',
        '&:hover': { backgroundColor: 'rgba(245,215,110,0.32)' },
        '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: '1px' },
      }}
    >
      {cycling ? `${navIndex + 1}/${count}` : (count > 1 ? `${count} spots` : '→')}
    </Box>
  );
}

function ElementRow({ node, selected, locations, navIndex, onSelect, onCycle }) {
  return (
    <Box
      component="button"
      type="button"
      aria-expanded={selected}
      onClick={onSelect}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, width: '100%', textAlign: 'left',
        font: 'inherit', fontSize: 12.5, cursor: 'pointer', border: 'none',
        px: 2, py: 0.9,
        color: selected ? 'white' : 'rgba(255,255,255,0.8)',
        backgroundColor: selected ? 'rgba(245,215,110,0.14)' : 'transparent',
        '&:hover': { backgroundColor: selected ? 'rgba(245,215,110,0.14)' : 'rgba(255,255,255,0.06)' },
        '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: '-2px' },
      }}
    >
      {/* Spans, not divs: these are inside a <button>, whose content model is
          phrasing content. */}
      <Box component="span" sx={{
        width: 7, height: 7, borderRadius: '2px', flexShrink: 0,
        backgroundColor: TYPE_DOT[node.type] || GOLD,
      }} />
      <Box component="span" sx={{
        flex: 1, minWidth: 0, overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {node.name}
      </Box>
      <LocationTag locations={locations} navIndex={navIndex} onCycle={onCycle} />
    </Box>
  );
}

/** Which chapter a cycling element is currently parked on. */
function ViewingLine({ name }) {
  return (
    <Typography sx={{
      px: 2, pb: 0.75, pl: '39px', fontSize: 10,
      color: 'rgba(255,255,255,0.5)',
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>
      Viewing: <Box component="span" sx={{ color: GOLD }}>{name}</Box>
    </Typography>
  );
}

/** One end of a relationship: its type dot and its name. */
function RefChip({ desc, bold }) {
  return (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Box component="span" sx={{
        width: 7, height: 7, borderRadius: '2px', flexShrink: 0,
        backgroundColor: desc.missing ? 'rgba(255,255,255,0.22)' : (TYPE_DOT[desc.type] || GOLD),
      }} />
      <Box
        component="span"
        title={desc.missing ? 'This no longer exists in the manuscript' : desc.name}
        sx={{
          minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontWeight: bold ? 600 : 400,
          fontStyle: desc.missing ? 'italic' : 'normal',
          color: desc.missing ? 'rgba(255,255,255,0.4)' : 'inherit',
        }}
      >
        {desc.missing ? 'unplaced' : desc.name}
      </Box>
    </Box>
  );
}

/**
 * One relationship, read left to right.
 *
 * Two lines rather than one: "Actor 1 — appears in — Chapter One" does not fit
 * 280px without truncating the names, which are the part worth reading. The
 * subject leads, the label and its far end sit under it.
 */
function RelationshipRow({ from, to, label, selected, onSelect }) {
  return (
    <Box
      component="button"
      type="button"
      aria-expanded={selected}
      onClick={onSelect}
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.25,
        width: '100%', textAlign: 'left', font: 'inherit', fontSize: 12.5,
        cursor: 'pointer', border: 'none', px: 2, py: 0.85,
        color: selected ? 'white' : 'rgba(255,255,255,0.8)',
        backgroundColor: selected ? 'rgba(245,215,110,0.14)' : 'transparent',
        '&:hover': { backgroundColor: selected ? 'rgba(245,215,110,0.14)' : 'rgba(255,255,255,0.06)' },
        '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: '-2px' },
      }}
    >
      <RefChip desc={from} bold />
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: '15px', minWidth: 0 }}>
        <Box component="span" sx={{
          flexShrink: 0, fontSize: 10.5, color: 'rgba(255,255,255,0.5)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110,
        }}>
          {label}
        </Box>
        <Box component="span" sx={{ flexShrink: 0, fontSize: 10.5, color: 'rgba(255,255,255,0.3)' }}>&#8594;</Box>
        <RefChip desc={to} />
      </Box>
    </Box>
  );
}

function EmptyNote({ children }) {
  return (
    <Typography sx={{
      color: 'rgba(255,255,255,0.45)', fontSize: 11.5, lineHeight: 1.6,
      px: 2, pb: 1,
    }}>
      {children}
    </Typography>
  );
}

export default function StoryElementsPanel({
  draftId,
  // Bumped by the editor's socket when another collaborator changes the board.
  // A counter, not the change: the server sends a signal and this re-reads.
  refreshToken = 0,
  // Told the board whenever it changes here, so the editor's element
  // recognition sees an element added or renamed in this panel without a
  // refetch. Never told the empty board the panel starts with.
  onBoardChange,
  outline = [],
  wordCounts = {},
  activeBlockId,
  // The draft's saved manuscriptStructure, straight off GET /api/drafts/<id>.
  // Already on the editor's doc, so the panel needs no fetch of its own.
  structure = null,
  // Opens the Manuscript Structure page on a given element.
  onOpenStructureElement,
  onJump,
  onInsertAfter,
  onInsertAtStart,
  onRename,
  onDelete,
  onPromote,
  onDemote,
  onReorder,
}) {
  const [view, setView] = useState('elements');
  const [board, setBoard] = useState({ nodes: [], edges: [] });
  const [loadError, setLoadError] = useState(null);
  // A failed *write* is not a failed *load*, and the two belong in different
  // places. `loadError` is about the panel — the board would not come down, and
  // the top of the panel is where that belongs, because there is nothing below
  // it to look at. `writeError` is about the row the author just clicked, and
  // reporting it at the top of a scrolling list put it off screen: the whole
  // reason a failed relationship delete read as "the X button does nothing"
  // rather than as an error. It renders in the details block instead, beside
  // the control that failed. Every write below is raised from a control inside
  // that block, so there is nowhere else for it to belong.
  const [writeError, setWriteError] = useState(null);
  const [addAnchor, setAddAnchor] = useState(null);
  const [creating, setCreating] = useState(null); // 'scene' | 'actor' | 'artifact'
  // One selection across both views, held as a reference string — "node:<id>"
  // or "chapter:<blockId>" — the same vocabulary edges use, so a row and an
  // edge endpoint are directly comparable.
  const [selectedRef, setSelectedRef] = useState(null);
  // Which Outline row the details are anchored under. The same element can be
  // the subject of several relationships, so selectedRef alone would expand the
  // details beneath every one of its rows.
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  // Which of the selected element's locations the manuscript is parked on.
  // null when nothing is being walked through.
  const [navIndex, setNavIndex] = useState(null);

  // The panel body is the one scroll container, so the sections flow together
  // rather than each getting its own scrollbar. NavigationPanel's drag
  // auto-scroll needs to move *this* element, which is why it takes the ref.
  const bodyRef = useRef(null);

  const cancelledRef = useRef(false);
  // Whether `board` has ever come from the server. Until it has, it is the
  // empty placeholder, and reporting that up would un-highlight every name in
  // the manuscript for as long as the first load takes — or for good, if it
  // fails.
  const boardLoadedRef = useRef(false);

  const loadBoard = useCallback(async () => {
    if (!draftId) return;
    try {
      const res = await storyElementApi.get(draftId);
      if (!cancelledRef.current) {
        boardLoadedRef.current = true;
        setBoard(res?.storyElements || { nodes: [], edges: [] });
        setLoadError(null);
      }
    } catch (err) {
      // A board that won't load must not take the chapter list down with it —
      // chapters come from the document, not the server.
      if (!cancelledRef.current) {
        setLoadError(orFallback(err, 'Could not load story elements.'));
      }
    }
  }, [draftId]);

  // Reloads on mount, and whenever the socket says a collaborator changed the
  // board. Every mutation below updates local state instead of refetching, so
  // the open row and the scroll position survive an edit of your own; this is
  // how someone else's edit gets in.
  useEffect(() => {
    cancelledRef.current = false;
    loadBoard();
    return () => { cancelledRef.current = true; };
  }, [loadBoard, refreshToken]);

  // Belt and braces for the socket: a dropped connection, a missed event, or a
  // tab that was asleep. Cheap, and it means the board is never stale for
  // longer than it takes to come back to the window.
  useEffect(() => {
    const refresh = () => { if (!document.hidden) loadBoard(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [loadBoard]);

  useEffect(() => {
    if (boardLoadedRef.current) onBoardChange?.(board);
    // Only the board: the callback is an inline arrow that changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board]);

  const byType = useMemo(() => {
    const groups = { scene: [], actor: [], artifact: [] };
    (board.nodes || []).forEach((n) => { if (groups[n.type]) groups[n.type].push(n); });
    return groups;
  }, [board.nodes]);

  // Front and back matter the author has switched on. Derived, not fetched:
  // these belong to the draft, not to the story-elements board, so they are
  // untouched by loadBoard and by the collaborator socket.
  const structureRows = useMemo(() => editorStructureRows(structure), [structure]);

  const chapters = useMemo(() => flattenOutline(outline), [outline]);
  const chapterCount = chapters.length;

  const subject = useMemo(
    () => (selectedRef
      ? describeRef(selectedRef, { nodes: board.nodes || [], chapters })
      : null),
    [selectedRef, board.nodes, chapters],
  );

  const sentences = useMemo(
    () => relationshipSentences({
      edges: board.edges || [], nodes: board.nodes || [], chapters,
    }),
    [board.edges, board.nodes, chapters],
  );

  const closeAddMenu = () => setAddAnchor(null);

  // ---- selection ----------------------------------------------------------

  const locationsOf = (ref) => locationsFor(ref, {
    edges: board.edges || [], nodes: board.nodes || [], chapters,
  });

  const clearSelection = () => {
    setSelectedRef(null);
    setSelectedEdgeId(null);
    setNavIndex(null);
    // The message belongs to the row being closed, not to the next one.
    setWriteError(null);
  };

  /**
   * Select a row, and move the manuscript to where that object lives.
   *
   * Both, always — that is the ticket's "selecting an object updates the page".
   * A chapter is its own location; anything else jumps to the first chapter it
   * appears in, which for an actor may be reached through a scene.
   */
  const selectRef = (ref, edgeId = null) => {
    const sameRow = selectedRef === ref && selectedEdgeId === edgeId;
    if (sameRow) { clearSelection(); return; }

    setSelectedRef(ref);
    setSelectedEdgeId(edgeId);
    setWriteError(null);

    const parsed = parseRef(ref);
    if (parsed?.kind === 'chapter') {
      onJump?.(parsed.id);
      setNavIndex(null);
      return;
    }
    const locs = locationsOf(ref);
    if (locs.length) {
      onJump?.(locs[0].blockId);
      setNavIndex(0);
    } else {
      setNavIndex(null);
    }
  };

  /**
   * Walk to the next chapter this element appears in.
   *
   * Cycling rather than listing: the panel is 280px beside the manuscript, and
   * what the author wants is to *be* at each place in turn, not to read a list
   * of them. Wraps, so the control never dead-ends.
   */
  const cycleLocation = (ref) => {
    const locs = locationsOf(ref);
    if (!locs.length) return;
    const next = (selectedRef === ref && navIndex != null) ? (navIndex + 1) % locs.length : 0;
    setSelectedRef(ref);
    setNavIndex(next);
    onJump?.(locs[next].blockId);
  };

  const selectChapter = (blockId) => {
    if (!blockId) return;
    selectRef(`chapter:${blockId}`);
  };

  // ---- board mutations ----------------------------------------------------
  //
  // Each of these updates local state from the server's response rather than
  // refetching the board: a refetch would flash the panel and close the row the
  // author is working in.

  const patchNode = (nodeId, fields) => setBoard((prev) => ({
    ...prev,
    nodes: (prev.nodes || []).map((n) => (String(n._id) === String(nodeId) ? { ...n, ...fields } : n)),
  }));

  // The local patch already happened on each keystroke, so this only writes.
  const saveNode = async (nodeId, fields) => {
    setWriteError(null);
    try {
      await storyElementApi.updateNode(draftId, nodeId, fields);
      return true;
    } catch (err) {
      setWriteError(orFallback(err, 'Could not save that change.'));
      return false;
    }
  };

  const removeNode = async (nodeId, name) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove "${name || 'this element'}" and its relationships?`)) return;
    setWriteError(null);
    const ref = `node:${nodeId}`;
    try {
      await storyElementApi.removeNode(draftId, nodeId);
      // The server drops the incident edges in the same write; mirror that here
      // rather than leaving relationships on screen that no longer exist.
      setBoard((prev) => ({
        ...prev,
        nodes: (prev.nodes || []).filter((n) => String(n._id) !== String(nodeId)),
        edges: (prev.edges || []).filter((e) => e.from !== ref && e.to !== ref),
      }));
      if (selectedRef === ref) clearSelection();
    } catch (err) {
      setWriteError(orFallback(err, 'Could not remove that element.'));
    }
  };

  const addRelationship = async ({ from, to, label }) => {
    setWriteError(null);
    try {
      const edge = await storyElementApi.createEdge(draftId, { from, to, label });
      setBoard((prev) => ({ ...prev, edges: [...(prev.edges || []), edge] }));
    } catch (err) {
      setWriteError(orFallback(err, 'Could not connect those.'));
    }
  };

  const relabelRelationship = async (edgeId, label) => {
    setWriteError(null);
    setBoard((prev) => ({
      ...prev,
      edges: (prev.edges || []).map((e) => (String(e._id) === String(edgeId) ? { ...e, label } : e)),
    }));
    try {
      await storyElementApi.updateEdge(draftId, edgeId, label);
    } catch (err) {
      setWriteError(orFallback(err, 'Could not rename that relationship.'));
    }
  };

  const removeRelationship = async (edgeId) => {
    setWriteError(null);
    try {
      await storyElementApi.removeEdge(draftId, edgeId);
      setBoard((prev) => ({
        ...prev,
        edges: (prev.edges || []).filter((e) => String(e._id) !== String(edgeId)),
      }));
      // In the Outline view the details are anchored to this row, which is
      // about to stop existing.
      if (selectedEdgeId === edgeId) clearSelection();
    } catch (err) {
      setWriteError(orFallback(err, 'Could not remove that relationship.'));
    }
  };

  const details = subject ? (
    <StoryElementDetails
      subject={subject}
      board={board}
      chapters={chapters}
      error={writeError}
      onPatchNode={patchNode}
      onSaveNode={saveNode}
      onRemoveNode={removeNode}
      onAddRelationship={addRelationship}
      onRelabelRelationship={relabelRelationship}
      onRemoveRelationship={removeRelationship}
    />
  ) : null;

  // "+ Chapter" is not a board write at all: a chapter is a heading in the
  // manuscript, so it goes through the editor's own insertChapter — which also
  // lays in a page break. The other three open the describe-and-connect dialog.
  // Insert after the chapter the author is currently in, not at the end of the
  // book. `activeBlockId` is the editor's scroll-spy pick — the chapter on
  // screen — so "Add a chapter" lands where they are working. Falls back to
  // appending when the caret isn't in a chapter yet, which is also what a
  // manuscript with no headings at all needs.
  const addChapter = () => {
    closeAddMenu();
    if (activeBlockId) onInsertAfter?.(activeBlockId);
    else onInsertAtStart?.();
  };
  const openCreate = (type) => { closeAddMenu(); setCreating(type); };

  // The new element joins the board in place; refetching would flash the whole
  // panel and lose the author's scroll position for no gain.
  const handleCreated = ({ node, edge }) => {
    setBoard((prev) => ({
      ...prev,
      nodes: [...(prev.nodes || []), node],
      edges: edge ? [...(prev.edges || []), edge] : (prev.edges || []),
    }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 1, px: 1.5, pt: 1.25, flexShrink: 0,
      }}>
        {/* Names what the view below is, as the prototype's header does. */}
        <Typography sx={{
          minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
        }}>
          {view === 'outline' ? 'Document outline' : 'Story elements'}
        </Typography>
        <Box
          component="button"
          type="button"
          aria-haspopup="menu"
          aria-expanded={Boolean(addAnchor)}
          onClick={(e) => setAddAnchor(e.currentTarget)}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.25,
            font: 'inherit', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
            color: GOLD, backgroundColor: 'transparent',
            border: `1px solid ${GOLD}`, borderRadius: '6px',
            px: 0.9, py: 0.3,
            '&:hover': { backgroundColor: 'rgba(245,215,110,0.15)' },
            '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: '1px' },
          }}
        >
          <AddIcon sx={{ fontSize: 14 }} /> Add
          <ExpandMoreIcon sx={{ fontSize: 15 }} />
        </Box>
      </Box>

      <Menu anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={closeAddMenu}>
        <MenuItem onClick={addChapter}>
          <Box sx={{ width: 9, height: 9, borderRadius: '3px', mr: 1.25, backgroundColor: TYPE_DOT.chapter }} />
          Chapter
        </MenuItem>
        {ELEMENT_SECTIONS.map(({ type, label }) => (
          <MenuItem key={type} onClick={() => openCreate(type)}>
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', mr: 1.25, backgroundColor: TYPE_DOT[type] }} />
            {label.replace(/s$/, '')}
          </MenuItem>
        ))}
      </Menu>

      <StoryElementModal
        open={Boolean(creating)}
        type={creating}
        draftId={draftId}
        nodes={board.nodes || []}
        outline={outline}
        onClose={() => setCreating(null)}
        onCreated={handleCreated}
      />

      <StoryElementsTabs value={view} onChange={setView} />

      <Box ref={bodyRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', color: 'white' }}>
        {/* One banner for the whole panel — this used to render once per empty
            section, so a failed load said the same thing three times. */}
        {loadError && (
          <Typography sx={{
            mx: 1, mt: 1, px: 1.25, py: 1, borderRadius: '4px', fontSize: 11.5, lineHeight: 1.6,
            color: '#F3C0B4', backgroundColor: 'rgba(200,80,60,0.18)',
          }}>
            {loadError}
          </Typography>
        )}
        {view === 'elements' ? (
          <>
            {/* No per-section add control: every kind of element is created
                from the one Add menu above, as the ticket asks. */}
            <SectionHeader label="Chapters" count={chapterCount} />
            <NavigationPanel
              outline={outline}
              wordCounts={wordCounts}
              activeBlockId={activeBlockId}
              onInsertAfter={onInsertAfter}
              onRename={onRename}
              onDelete={onDelete}
              onPromote={onPromote}
              onDemote={onDemote}
              onJump={selectChapter}
              onReorder={onReorder}
              scrollRef={bodyRef}
              renderAfterRow={(node) =>
                (selectedRef === `chapter:${node.blockId}` ? details : null)}
            />

            {/* Manuscript pages — dedication, foreword, appendix and the rest.
                They are not board nodes and cannot be added from the Add menu;
                the Structure page is where they are switched on and written. */}
            <SectionHeader label="Manuscript Pages" count={structureRows.length} />
            {structureRows.length === 0 ? (
              <EmptyNote>
                No front or back matter yet — switch a page on in Manuscript
                Structure, under the File tab, and it is listed here.
              </EmptyNote>
            ) : (
              structureRows.map((row) => (
                <StructureRow
                  key={row.id}
                  name={row.name}
                  onOpen={() => onOpenStructureElement?.(row.id)}
                />
              ))
            )}

            {ELEMENT_SECTIONS.map(({ type, label }) => (
              <React.Fragment key={type}>
                <SectionHeader label={label} count={byType[type].length} />
                {byType[type].length === 0 ? (
                  <EmptyNote>
                    No {label.toLowerCase()} yet — add one from the Add menu above.
                  </EmptyNote>
                ) : (
                  byType[type].map((node) => {
                    const ref = `node:${node._id}`;
                    const isSelected = selectedRef === ref;
                    const locs = locationsOf(ref);
                    const at = isSelected && navIndex != null && locs[navIndex] ? navIndex : null;
                    return (
                      <React.Fragment key={node._id}>
                        <ElementRow
                          node={node}
                          selected={isSelected}
                          locations={locs}
                          navIndex={at}
                          onSelect={() => selectRef(ref)}
                          onCycle={() => cycleLocation(ref)}
                        />
                        {at != null ? <ViewingLine name={locs[at].name} /> : null}
                        {isSelected ? details : null}
                      </React.Fragment>
                    );
                  })
                )}
              </React.Fragment>
            ))}
          </>
        ) : (
          <Box sx={{ pt: 1 }}>
            {sentences.length === 0 ? (
              <EmptyNote>
                No relationships yet. Connect an element from the Elements tab —
                an actor in a scene, an actor using an artifact — and each
                connection is listed here.
              </EmptyNote>
            ) : (
              sentences.map(({ edge, from, to, label }) => {
                const isSelected = selectedEdgeId === edge._id;
                return (
                  <React.Fragment key={edge._id}>
                    <RelationshipRow
                      from={from}
                      to={to}
                      label={label}
                      selected={isSelected}
                      onSelect={() => selectRef(from.ref, edge._id)}
                    />
                    {isSelected ? details : null}
                  </React.Fragment>
                );
              })
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
